import { Component, OnInit, Inject, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { TutorialVideosService, TutorialVideo, Category } from '../../page/company/support/services/tutorial-videos.service';

/**
 * Category labels mapping
 */
export const CategoryLabels: Record<string, string> = {
  'FINANCEIRO': 'Financeiro',
  'GESTAO_NOTAS': 'Gestão de Notas',
  'OFICINA': 'Oficina',
  'PEDIDOS': 'Pedidos',
  'GESTAO_EMPRESA': 'Gestão da Empresa',
  'PECAS_ESTOQUE': 'Peças/Estoque'
};

/**
 * Route to Category mapping
 * Maps URL segments to video categories
 */
export const RouteToCategory: Record<string, string> = {
  'financial': 'FINANCEIRO',
  'cash-title': 'FINANCEIRO',
  'receipt': 'FINANCEIRO',
  'banks': 'FINANCEIRO',
  'conciliation': 'FINANCEIRO',
  'boletos': 'FINANCEIRO',
  'invoices': 'GESTAO_NOTAS',
  'dfe': 'GESTAO_NOTAS',
  'xml': 'GESTAO_NOTAS',
  'sintegra': 'GESTAO_NOTAS',
  'home': 'OFICINA',  
  'os': 'OFICINA',
  'opened': 'OFICINA',
  'closed': 'OFICINA',
  'ended': 'OFICINA',
  'budget': 'OFICINA',
  'rejected': 'OFICINA',
  'labors': 'OFICINA',
  'vehicles': 'OFICINA',
  'machines': 'OFICINA',
  'mechanical': 'OFICINA',
  'order': 'PEDIDOS',
  'pdv': 'PEDIDOS',
  'parts': 'PECAS_ESTOQUE',
  'stock': 'PECAS_ESTOQUE',
  'parts-consult': 'PECAS_ESTOQUE',
  'clients': 'GESTAO_EMPRESA',
  'suppliers': 'GESTAO_EMPRESA',
  'users': 'GESTAO_EMPRESA',
  'calendar': 'GESTAO_EMPRESA',
  'dashboard': 'GESTAO_EMPRESA',
  'kpi': 'GESTAO_EMPRESA',
  'crm': 'GESTAO_EMPRESA',
  'settings': 'GESTAO_EMPRESA'
};

@Component({
  selector: 'app-tutorial-videos-widget',
  templateUrl: './tutorial-videos-widget.component.html',
  styleUrls: ['./tutorial-videos-widget.component.scss'],
  animations: [
    trigger('slidePanel', [
      state('closed', style({
        transform: 'translateX(-100%)',
        opacity: 0
      })),
      state('open', style({
        transform: 'translateX(0)',
        opacity: 1
      })),
      transition('closed <=> open', animate('300ms ease-in-out'))
    ])
  ]
})
export class TutorialVideosWidgetComponent implements OnInit {
  // Widget state
  isOpen = false;

  // Form control for search
  searchControl = new FormControl('');

  // Available categories
  categories: Category[] = [];
  
  // Category labels
  categoryLabels = CategoryLabels;
  
  // Selected category filter (null = all categories)
  selectedCategory: string | null = null;

  // Tutorial videos from API
  videos: TutorialVideo[] = [];

  // Filtered videos based on search and category
  filteredVideos: TutorialVideo[] = [];

  // Grouped videos by category
  groupedVideos: { category: Category; videos: TutorialVideo[] }[] = [];

  // Track expanded state for each category (show all videos)
  expandedCategories: Set<string> = new Set();

  // Number of videos to show initially per category
  readonly INITIAL_VIDEOS_PER_CATEGORY = 3;

  // Loading state
  loading = true;

  // Flag to track if data was already loaded
  private dataLoaded = false;

  // Auto-detected category from current route
  autoDetectedCategory: string | null = null;

  constructor(
    private dialog: MatDialog,
    private tutorialVideosService: TutorialVideosService,
    private router: Router
  ) { }

  ngOnInit(): void {
    // Subscribe to search changes
    this.searchControl.valueChanges.subscribe(() => {
      this.filterVideos();
    });

    // Don't load data on init - wait for user to open the widget
  }

  /**
   * Toggle widget open/close
   */
  toggle(): void {
    this.isOpen = !this.isOpen;
    
    // Load data only when opening for the first time
    if (this.isOpen && !this.dataLoaded) {
      // Detect category from current route
      this.detectCategoryFromRoute();
      
      this.loadData();
      this.dataLoaded = true;
    } else if (this.isOpen) {
      // Re-detect category when opening again (user may have navigated)
      this.detectCategoryFromRoute();
      this.filterVideos();
    }
  }

  /**
   * Detect category from current route
   */
  private detectCategoryFromRoute(): void {
    const currentUrl = this.router.url;
    
    // Try to find a matching category from URL segments
    for (const routeSegment in RouteToCategory) {
      if (currentUrl.includes(`/${routeSegment}`)) {
        this.autoDetectedCategory = RouteToCategory[routeSegment];
        this.selectedCategory = this.autoDetectedCategory;
        return;
      }
    }
    
    // No match found - show all videos
    this.autoDetectedCategory = null;
    this.selectedCategory = null;
  }

  /**
   * Close widget
   */
  close(): void {
    this.isOpen = false;
  }

  /**
   * Load videos and categories from API
   */
  loadData(): void {
    this.loading = true;

    // Load videos
    this.tutorialVideosService.getAllVideos().subscribe({
      next: (videos) => {
        this.videos = videos;
        this.filteredVideos = videos;
        this.groupVideos();
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading videos:', error);
        this.loading = false;
      }
    });

    // Load categories
    this.tutorialVideosService.getCategories().subscribe({
      next: (categories) => {
        this.categories = categories;
        // Update category labels with API data
        categories.forEach(cat => {
          this.categoryLabels[cat.key] = cat.label;
        });
        // Regroup if videos are already loaded
        if (this.videos.length > 0) {
          this.groupVideos();
        }
      },
      error: (error) => {
        console.error('Error loading categories:', error);
      }
    });
  }

  /**
   * Remove accents from string for better search matching
   */
  private removeAccents(str: string): string {
    return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }

  /**
   * Filter videos based on search term and selected category
   */
  filterVideos(): void {
    const searchTerm = this.searchControl.value?.toLowerCase() || '';
    const normalizedSearchTerm = this.removeAccents(searchTerm);
    
    this.filteredVideos = this.videos.filter(video => {
      // Category filter (from auto-detection or manual selection)
      const categoryMatch = !this.selectedCategory || video.category === this.selectedCategory;
      
      // Search filter with accent-insensitive matching
      const searchMatch = !searchTerm || 
        this.removeAccents(video.title.toLowerCase()).includes(normalizedSearchTerm) ||
        video.keywords.some(keyword => this.removeAccents(keyword.toLowerCase()).includes(normalizedSearchTerm)) ||
        this.removeAccents(video.category.toLowerCase()).includes(normalizedSearchTerm);

      return categoryMatch && searchMatch;
    });

    // Group videos by category
    this.groupVideos();
  }

  /**
   * Group filtered videos by category
   */
  groupVideos(): void {
    this.groupedVideos = [];

    // Create a map of categories with videos
    const categoryMap = new Map<string, TutorialVideo[]>();

    this.filteredVideos.forEach(video => {
      if (!categoryMap.has(video.category)) {
        categoryMap.set(video.category, []);
      }
      categoryMap.get(video.category)?.push(video);
    });

    // Convert map to array and sort by category order from API
    this.categories.forEach(category => {
      const videos = categoryMap.get(category.key);
      if (videos && videos.length > 0) {
        this.groupedVideos.push({
          category: category,
          videos: videos
        });
      }
    });
  }

  /**
   * Clear all filters (including auto-detected category)
   */
  clearFilters(): void {
    this.searchControl.setValue('');
    this.selectedCategory = null;
    this.autoDetectedCategory = null;
    this.filterVideos();
  }

  /**
   * Show all videos (clear category filter but keep search)
   */
  showAllVideos(): void {
    this.selectedCategory = null;
    this.autoDetectedCategory = null;
    this.filterVideos();
  }

  /**
   * Get visible videos for a category (limited or all)
   */
  getVisibleVideos(categoryKey: string, allVideos: TutorialVideo[]): TutorialVideo[] {
    if (this.expandedCategories.has(categoryKey)) {
      return allVideos;
    }
    return allVideos.slice(0, this.INITIAL_VIDEOS_PER_CATEGORY);
  }

  /**
   * Check if category has more videos than initially shown
   */
  hasMoreVideos(categoryKey: string, totalVideos: number): boolean {
    return !this.expandedCategories.has(categoryKey) && totalVideos > this.INITIAL_VIDEOS_PER_CATEGORY;
  }

  /**
   * Get count of hidden videos
   */
  getHiddenVideosCount(totalVideos: number): number {
    return totalVideos - this.INITIAL_VIDEOS_PER_CATEGORY;
  }

  /**
   * Toggle expand/collapse for a category
   */
  toggleCategoryExpansion(categoryKey: string): void {
    if (this.expandedCategories.has(categoryKey)) {
      this.expandedCategories.delete(categoryKey);
    } else {
      this.expandedCategories.add(categoryKey);
    }
  }

  /**
   * Check if category is expanded
   */
  isCategoryExpanded(categoryKey: string): boolean {
    return this.expandedCategories.has(categoryKey);
  }

  /**
   * Open video in dialog (fullscreen mode)
   */
  openVideoDialog(video: TutorialVideo): void {
    this.dialog.open(VideoDialogComponent, {
      width: '1200px',
      maxWidth: '90vw',
      panelClass: 'video-dialog-container',
      data: video
    });
  }

  /**
   * Get Vimeo thumbnail URL
   */
  getVimeoThumbnailUrl(vimeoId: string): string {
    if (vimeoId.includes('/')) {
      const [id, hash] = vimeoId.split('/');
      return `https://vumbnail.com/${id}:${hash}.jpg`;
    }
    return `https://vumbnail.com/${vimeoId}.jpg`;
  }

  /**
   * Track by function for ngFor optimization
   */
  trackByVideoId(index: number, video: TutorialVideo): string {
    return video._id;
  }
}

/**
 * Video Dialog Component (reused from support component)
 */
@Component({
  selector: 'app-video-dialog-widget',
  template: `
    <div class="video-dialog">
      <div class="dialog-header">
        <h2>
          <mat-icon>play_circle</mat-icon>
          {{ data.title }}
        </h2>
        <button mat-icon-button mat-dialog-close>
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <div class="dialog-content">
        <div class="video-container-dialog">
          <iframe 
            [src]="getVimeoEmbedUrl(data.vimeoId) | safe: 'resourceUrl'"
            frameborder="0" 
            allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share" 
            referrerpolicy="strict-origin-when-cross-origin"
            allowfullscreen
            [title]="data.title">
          </iframe>
        </div>
      </div>

      <div class="dialog-footer">
        <div class="video-info">
          <p *ngIf="data.description">{{ data.description }}</p>
          <span class="category-chip">{{ getCategoryLabel(data.category) }}</span>
        </div>
        
        <div class="keywords" *ngIf="data.keywords && data.keywords.length">
          <span class="keyword-chip" *ngFor="let keyword of data.keywords">
            <mat-icon>label</mat-icon>
            {{ keyword }}
          </span>
        </div>
      </div>
    </div>
  `,
  styleUrls: ['./tutorial-videos-widget.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class VideoDialogComponent {
  constructor(@Inject(MAT_DIALOG_DATA) public data: TutorialVideo) { }

  getCategoryLabel(category: string): string {
    return CategoryLabels[category] || category;
  }

  getVimeoEmbedUrl(vimeoId: string): string {
    if (vimeoId.includes('/')) {
      const [id, hash] = vimeoId.split('/');
      return `https://player.vimeo.com/video/${id}?h=${hash}&badge=0&autopause=0&title=0&sidedock=0&vimeo_logo=0&cc=0&transcript=0&autoplay=1&player_id=0&app_id=58479`;
    }
    return `https://player.vimeo.com/video/${vimeoId}?badge=0&autopause=0&title=0&sidedock=0&vimeo_logo=0&cc=0&transcript=0&autoplay=1&player_id=0&app_id=58479`;
  }
}
